import { useState, forwardRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";
import portalOneLogo from "@/assets/portalone-suite-logo.png";
import { LoginDropdown } from "@/components/LoginDropdown";
import { ForgotPasswordModal } from "@/components/ForgotPasswordModal";
import { PortalSubscriptionModal } from "@/components/PortalSubscriptionModal";
import { ThemeToggle } from "@/components/theme-toggle";
import { useSystems } from "@/hooks/use-plans";
import { useSystemPlans } from "@/hooks/use-plans";
import { PublicPlanCard } from "@/components/plans/PublicPlanCard";
import { Footer } from "@/components/footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

const brand = {
  cyan: "#007CBE",
  neon: "#A3FF3F",
  graphite: "#111111",
  fog: "#E5E5E5",
};

const fadeIn = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

function GlowHalo({ className = "" }: { className?: string }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 -z-10 blur-3xl ${className}`}
      aria-hidden
    >
      <div
        className="absolute -top-24 -left-24 h-72 w-72 rounded-full"
        style={{ 
          background: `radial-gradient(60% 60% at 50% 50%, ${brand.cyan}66 0%, transparent 65%)`,
        }}
      />
      <div
        className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full"
        style={{ 
          background: `radial-gradient(60% 60% at 50% 50%, ${brand.neon}66 0%, transparent 65%)`,
        }}
      />
    </div>
  );
}

function NeonButton({ children, className = "", ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`group relative overflow-hidden rounded-2xl px-5 py-3 font-medium text-black shadow-md transition hover:shadow-lg focus:outline-none ${className}`}
      style={{
        background: `linear-gradient(90deg, ${brand.neon}, ${brand.cyan})`,
        boxShadow: `0 0 30px ${brand.neon}44, 0 0 50px ${brand.cyan}33`,
      }}
      {...props}
    >
      <span className="relative z-10 flex items-center gap-2">
        {children}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
      <span
        className="absolute inset-0 -z-0 opacity-0 transition-opacity group-hover:opacity-20"
        style={{ background: `radial-gradient(80% 80% at 50% 50%, white, transparent)` }}
      />
    </button>
  );
}

const LoginButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ children, ...props }, ref) => (
    <button
      ref={ref}
      className="group relative overflow-hidden rounded-2xl px-5 py-3 font-semibold text-black shadow-md transition hover:shadow-lg focus:outline-none"
      style={{ background: `linear-gradient(90deg, ${brand.neon}, ${brand.cyan})`, boxShadow: `0 0 30px ${brand.neon}44, 0 0 50px ${brand.cyan}33` }}
      {...props}
    >
      <span className="relative z-10 inline-flex items-center gap-2">{children}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5"/></span>
      <span className="absolute inset-0 -z-0 opacity-0 transition-opacity group-hover:opacity-20" style={{ background: `radial-gradient(80% 80% at 50% 50%, white, transparent)` }} />
    </button>
  )
);
LoginButton.displayName = "LoginButton";

export default function PublicPlansPage() {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const { data: systems = [], isLoading: systemsLoading } = useSystems();

  const handlePlanSelect = (systemCode: string) => {
    setShowSubscriptionModal(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300 selection:bg-primary/20 overflow-x-hidden">
      <GlowHalo />

      {/* Header */}
      <header className="fixed top-0 z-50 w-full backdrop-blur-sm bg-background/80 supports-[backdrop-filter]:bg-background/60 border-b border-border transition-colors duration-300">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center cursor-pointer">
            <img 
              src={portalOneLogo} 
              alt="PortalONE Suite" 
              className="h-16 md:h-20 w-auto object-contain" 
            />
          </Link>
          <nav className="hidden items-center gap-7 md:flex">
            <Link 
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Home
            </Link>
            {[
              ["Sistemas", "/#sistemas"],
              ["Benefícios", "/#beneficios"],
              ["Planos", "/plans"],
              ["Contato", "/contact"],
            ].map(([label, href]) => (
              <Link 
                key={label} 
                to={href as string}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LoginDropdown
              onForgotPassword={() => setShowForgotPassword(true)}
              onSignup={() => setShowSubscriptionModal(true)}
              triggerButton={<LoginButton>Login</LoginButton>}
            />
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div aria-hidden className="h-20 md:h-24" />

      <main className="relative">
        {/* Hero */}
        <section className="relative overflow-hidden py-20 transition-colors duration-300">
          <div
            className="absolute inset-0 -z-10 opacity-30"
            style={{
              background: `radial-gradient(120% 80% at 0% 0%, ${brand.cyan}55 0%, transparent 55%), radial-gradient(120% 80% at 100% 100%, ${brand.neon}55 0%, transparent 55%)`,
            }}
          />
          <div className="mx-auto max-w-7xl px-4 text-center">
            <motion.div variants={fadeIn} initial="hidden" animate="show">
              <h1 className="text-4xl font-bold leading-tight md:text-5xl text-foreground">
                Escolha o Plano Ideal para
                <span className="block bg-gradient-to-r from-[#A3FF3F] to-[#007CBE] bg-clip-text text-transparent">
                  Seu Negócio
                </span>
              </h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                Planos flexíveis para empresas de todos os portes. Escolha o sistema que precisa e escale conforme seu negócio cresce.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Individual Systems Section */}
        {!systemsLoading && systems.length > 0 && (
          <>
            {systems.map((system) => (
              <SystemPlansSection
                key={system.id}
                systemCode={system.code}
                systemName={system.name}
                systemDescription={system.description}
                onPlanSelect={handlePlanSelect}
              />
            ))}
          </>
        )}

        {/* Loading State */}
        {systemsLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}

        {/* CTA Section */}
        <section className="relative border-t border-border py-20 transition-colors duration-300 overflow-hidden">
          <GlowHalo />
          <div className="mx-auto max-w-4xl px-4 text-center">
            <motion.div variants={fadeIn} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <h2 className="text-3xl font-semibold text-foreground">
                Pronto para transformar sua gestão?
              </h2>
              <p className="mt-3 text-muted-foreground">
                Fale com nosso time comercial e descubra qual plano se encaixa melhor nas suas necessidades.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <NeonButton onClick={() => setShowSubscriptionModal(true)}>
                  Começar Agora
                </NeonButton>
                <Link to="/contact">
                  <button className="rounded-2xl border border-border px-5 py-3 font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                    Falar com Vendas
                  </button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Modals */}
      <ForgotPasswordModal 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword} 
      />
      <PortalSubscriptionModal
        open={showSubscriptionModal}
        onOpenChange={setShowSubscriptionModal}
      />
      <WhatsAppFloat />
    </div>
  );
}

// Component for individual system plans section
function SystemPlansSection({ 
  systemCode, 
  systemName, 
  systemDescription,
  onPlanSelect 
}: { 
  systemCode: string; 
  systemName: string; 
  systemDescription?: string | null;
  onPlanSelect: (systemCode: string) => void;
}) {
  const { data: plans = [], isLoading } = useSystemPlans(systemCode);

  // Filter only paid plans (exclude Trial/Free plans)
  const paidPlans = plans.filter(plan => 
    (plan.price_monthly && plan.price_monthly > 0) || 
    (plan.price_yearly && plan.price_yearly > 0)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (paidPlans.length === 0) return null;

  return (
    <section id={systemCode} className="relative border-t border-border py-20 transition-colors duration-300 overflow-hidden">
      <GlowHalo />
      <div className="mx-auto max-w-7xl px-4">
        <motion.div variants={fadeIn} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <Zap className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-semibold text-foreground">{systemName}</h2>
          </div>
          {systemDescription && (
            <p className="mt-2 text-muted-foreground max-w-2xl">
              {systemDescription}
            </p>
          )}
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {paidPlans.map((plan) => (
            <motion.div key={plan.id!} variants={fadeIn}>
              <PublicPlanCard
                plan={{
                  id: plan.id!,
                  name: plan.name!,
                  description: plan.description,
                  price_monthly: plan.price_monthly,
                  price_yearly: plan.price_yearly,
                  trial_days: (plan as any).trial_days || null,
                  features: plan.features,
                  is_popular: (plan as any).is_popular || false,
                }}
                systemCode={systemCode}
                onSelect={() => onPlanSelect(systemCode)}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
